import { DeleteButton } from "components/ui/action-button";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { Input } from "components/ui/input";
import { SelectPro } from "components/ui/select-pro";
import { FormikErrors, FormikTouched, FormikValues } from "formik";
import { InvoiceLineItemInput, InvoicePaymentScheduleInput } from "generated/graphql";
import React from "react";
import { isUrl } from "util/uri";
import { InvoiceLineItemsEditor } from "./invoice-line-items-editor";
import { InvoicePaymentScheduleEditor } from "./invoice-payment-schedule-editor";

interface DeleteConfirmContent {
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
}

interface InvoiceDetailsEditorProps {
    handleChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => void;
    handleBlur: (
        e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => void;
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void;
    setFieldTouched: (
        field: string,
        isTouched: boolean,
        shouldValidate?: boolean,
    ) => void;
    values: FormikValues;
    touched: FormikTouched<FormikValues>;
    errors: FormikErrors<FormikValues>;
    lineItems: InvoiceLineItemInput[];
    onLineItemsChange: (lineItems: InvoiceLineItemInput[]) => void;
    includePaidDate?: boolean;
    paymentSchedule: InvoicePaymentScheduleInput | null;
    onPaymentScheduleChange: (
        paymentSchedule: InvoicePaymentScheduleInput | null,
    ) => void;
    onDelete?: () => void;
    deleteConfirm?: DeleteConfirmContent;
    disabled?: boolean;
}

const defaultDeleteConfirm: DeleteConfirmContent = {
    title: "Delete invoice?",
    message: "Are you sure you want to delete this invoice?",
    confirmLabel: "Delete",
    cancelLabel: "Cancel",
};

export const InvoiceDetailsEditor: React.FC<InvoiceDetailsEditorProps> = props => {
    const {
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        lineItems,
        onLineItemsChange,
        includePaidDate,
        paymentSchedule,
        onPaymentScheduleChange,
        setFieldValue,
        setFieldTouched,
        onDelete,
        deleteConfirm,
        disabled,
    } = props;

    const commitDate = React.useCallback(
        (field: string) => (value: string | null) => {
            setFieldValue(field, value ?? "");
            setFieldTouched(field, true);
        },
        [setFieldValue, setFieldTouched],
    );

    const handlePaymentScheduleChange = React.useCallback(
        (paymentSchedule: InvoicePaymentScheduleInput | null) => {
            onPaymentScheduleChange(paymentSchedule);
            if (paymentSchedule) {
                const latestDueDate = paymentSchedule.items
                    .filter(x => !!x.dueDate)
                    .map(x => x.dueDate)
                    .sort((a, b) => {
                        const dateA = new Date(a);
                        const dateB = new Date(b);

                        return dateA.getTime() - dateB.getTime();
                    })
                    .reverse()[0];

                if (latestDueDate) {
                    setFieldValue("dueDate", latestDueDate);
                } else {
                    setFieldValue("dueDate", "");
                }

                if (paymentSchedule.items.every(x => !!x.paidDate)) {
                    const latestPaidDate = paymentSchedule.items
                        .map(x => x.paidDate)
                        .sort((a, b) => {
                            const dateA = new Date(a);
                            const dateB = new Date(b);

                            return dateA.getTime() - dateB.getTime();
                        })
                        .reverse()[0];

                    if (latestPaidDate) {
                        setFieldValue("paidDate", latestPaidDate);
                    }
                } else {
                    setFieldValue("paidDate", "");
                }
            }
        },
        [onPaymentScheduleChange, setFieldValue],
    );

    return (
        <>
            <div className="flex justify-between items-center">
                <div>
                    <div className="text-xl font-semibold">Invoice Details</div>
                    {disabled &&
                    values.externalReference &&
                    isUrl(values.externalReference) ? (
                        <a
                            className="text-sm font-semibold text-brand-600 hover:underline"
                            href={values.externalReference}
                            target="_blank"
                            rel="noreferrer">
                            View original
                        </a>
                    ) : null}
                </div>
                {onDelete ? (
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={onDelete}
                        confirm={deleteConfirm ?? defaultDeleteConfirm}
                    />
                ) : null}
            </div>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <div className="">
                        <SelectPro
                            includePlaceholderOption={false}
                            label="Status"
                            name="status"
                            onChange={handleChange}
                            value={values.status}
                            disabled={disabled}
                            options={[
                                { value: "new", text: "New" },
                                { value: "sent", text: "Sent" },
                                { value: "paid", text: "Paid" },
                                { value: "void", text: "Void" },
                            ]}
                        />
                    </div>
                    <div>
                        <DatePicker
                            label="Invoice sent date"
                            value={values.sentDate || null}
                            onChange={commitDate("sentDate")}
                            disabled={!!disabled}
                            required={false}
                            textSize="base"
                            onOpenChange={() => undefined}
                            minValue={null}
                            maxValue={null}
                            visibleYears={20}
                        />
                        {touched.sentDate && errors.sentDate ? (
                            <div className="text-red-700 font-bold mt-1">
                                {errors.sentDate as string}
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <Input
                            label="Invoice number"
                            placeholder="Leave blank to generate"
                            type="input"
                            name="serial"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.serial}
                            disabled={disabled}
                        />
                        {touched.serial && errors.serial ? (
                            <div className="text-red-700 font-bold mt-1">
                                {errors.serial as string}
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <DatePicker
                            label="Due date"
                            value={values.dueDate || null}
                            onChange={commitDate("dueDate")}
                            disabled={!!disabled || !!paymentSchedule}
                            required={false}
                            textSize="base"
                            onOpenChange={() => undefined}
                            minValue={null}
                            maxValue={null}
                            visibleYears={20}
                        />
                        {touched.dueDate && errors.dueDate ? (
                            <div className="text-red-700 font-bold mt-1">
                                {errors.dueDate as string}
                            </div>
                        ) : null}
                    </div>
                    {includePaidDate ? (
                        <DatePicker
                            label="Paid date"
                            value={values.paidDate || null}
                            onChange={commitDate("paidDate")}
                            disabled={!!disabled || !!paymentSchedule}
                            required={false}
                            textSize="base"
                            onOpenChange={() => undefined}
                            minValue={null}
                            maxValue={null}
                            visibleYears={20}
                        />
                    ) : null}
                </div>
                <InvoiceLineItemsEditor
                    lineItems={lineItems}
                    onChange={disabled ? undefined : onLineItemsChange}
                    paymentSchedule={paymentSchedule}
                    paidDate={values.paidDate}
                />
                <InvoicePaymentScheduleEditor
                    paymentSchedule={paymentSchedule}
                    onChange={disabled ? undefined : handlePaymentScheduleChange}
                    dueDate={values.dueDate}
                />
                <div className="w-full">
                    <div className="mb-1">
                        <label htmlFor="invoice-notes-field">Notes (optional)</label>
                    </div>
                    <textarea
                        className="form-input rounded w-full disabled:bg-gray-100 border-gray-400 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                        id="invoice-notes-field"
                        name="notes"
                        rows={3}
                        value={values.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={disabled}
                    />
                    {touched.notes && errors.notes ? (
                        <div className="text-red-700 font-bold mt-1">
                            {errors.notes as string}
                        </div>
                    ) : null}
                    <div className="italic text-sm">
                        Notes will be included in generated PDF invoices
                    </div>
                </div>
                <div>
                    <Input
                        type="text"
                        label="External Invoice Link"
                        name="externalReference"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.externalReference}
                        disabled={disabled}
                    />
                    <div className="text-xs italic">
                        Used to track a link to this invoice in another software platform,
                        such as QuickBooks Online.
                    </div>
                    {touched.externalReference && errors.externalReference ? (
                        <div className="text-red-700 font-bold mt-1">
                            {errors.externalReference as string}
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};
